import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TRARecentFormBars } from "../../../design";
import { ErrorState, LoadingState } from "../../../design/shell";
import { TRASignatureEmptyState } from "../../../design/visual";
import { useNavigateBack } from "../../../hooks/useNavigateBack";
import TRAButton from "../../../design/components/primitives/TRAButton";
import TRAMatchCard from "../../../design/components/sports/TRAMatchCard";
import RankingProfileBlock from "../../rankings/components/RankingProfileBlock";
import CareerStatsCards from "../../profile/components/ecosystem/CareerStatsCards";
import ActivityTimelineFeed from "../../profile/components/ecosystem/ActivityTimelineFeed";
import PersonalRecordsGrid from "../../profile/components/ecosystem/PersonalRecordsGrid";
import PersonalBestsGrid from "../../profile/components/ecosystem/PersonalBestsGrid";
import CrossSportSummary from "../../profile/components/ecosystem/CrossSportSummary";
import AchievementGallery from "../../profile/components/ecosystem/AchievementGallery";
import ActivityCalendar from "../../profile/components/ecosystem/ActivityCalendar";
import MilestonesTimeline from "../../profile/components/ecosystem/MilestonesTimeline";
import { recentFormLetters } from "../../../domain/profile/profileDashboardStats";
import { SPORT_FILTER_OVERALL } from "../../../domain/player/playerIntelligence";
import { resolveSportModule } from "../../../sports/registry";
import pp from "../../profile/styles/profileClaude.module.css";
import styles from "../styles/playerExperience.module.css";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "history", label: "History" },
  { id: "stats", label: "Stats" },
  { id: "achievements", label: "Achievements" },
];

/**
 * Prefer dashboard aggregates (full sport-scoped history). Never recompute
 * season / career rates from truncated `recentMatches` — that caused This Season
 * vs Stats mismatches.
 */
function resolveSeasonStats(dash) {
  const fromDash = dash?.stats?.season;
  if (fromDash && typeof fromDash === "object") {
    return {
      matches: fromDash.matches ?? 0,
      wins: fromDash.wins ?? 0,
      losses: fromDash.losses ?? 0,
      winRate: fromDash.winRate ?? null,
      year: fromDash.year,
    };
  }
  return { matches: 0, wins: 0, losses: 0, winRate: null };
}

/**
 * Player sports dashboard — Intelligence Foundation + Phase 2.2 polish.
 * Sport scope comes from Global Active Sport Mode (no in-page switcher).
 */
export default function PlayerProfileExperience({
  dash,
  ranking,
  loading,
  error,
  sportFilter = SPORT_FILTER_OVERALL,
  backFallback = "/profile",
}) {
  const goBack = useNavigateBack(backFallback);
  const [tab, setTab] = useState("overview");
  const matches = dash?.recentMatches || [];
  const form = useMemo(() => recentFormLetters(matches, 5), [matches]);
  const season = useMemo(() => resolveSeasonStats(dash), [dash]);

  if (loading && !dash) {
    return <LoadingState label="Loading player profile" layout="profile" />;
  }
  if (!dash) {
    return (
      <ErrorState
        title="Profile unavailable"
        description={
          error
            ? "We couldn’t load this player right now. Check your connection and try again."
            : "This profile isn’t available."
        }
        action={
          <TRAButton type="button" variant="secondary" onClick={goBack}>
            Go back
          </TRAButton>
        }
      />
    );
  }

  const rateInt =
    dash.stats?.careerWinPct == null
      ? null
      : Math.min(100, Math.max(0, Math.round(dash.stats.careerWinPct)));
  const activeSportLabel =
    sportFilter === SPORT_FILTER_OVERALL
      ? null
      : resolveSportModule(sportFilter)?.displayName || null;

  const highlight =
    dash.achievementFramework?.unlocked?.[0] ||
    dash.achievements?.[0] ||
    (ranking?.achievements || [])[0] ||
    null;

  const showCrossSport =
    sportFilter === SPORT_FILTER_OVERALL && (dash.crossSport?.sports?.length || 0) > 0;
  const emptyCopy = dash.emptyCopy || null;

  return (
    <div className={styles.dash} data-testid="player-profile-experience">
      <div className={styles.tabs} role="tablist" aria-label="Profile sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`${styles.tab} ${tab === t.id ? styles.tabActive : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" ? (
        <>
      <section className={pp.winRateBlock} aria-label="Career win rate">
        <p className={pp.kicker}>
          {sportFilter === SPORT_FILTER_OVERALL ? "Overall win rate" : "Career win rate"}
        </p>
        <div className={pp.bigRate}>
          <span className={pp.bigRateValue}>{rateInt == null ? "—" : rateInt}</span>
          {rateInt != null ? <span className={pp.bigRatePct}>%</span> : null}
        </div>
        {rateInt != null ? (
          <div className={pp.progressTrack} aria-hidden>
            <div className={pp.progressFill} style={{ width: `${rateInt}%` }} />
          </div>
        ) : null}
        {dash.stats?.currentWinningStreak > 0 ? (
          <p className={pp.metaLine} style={{ marginTop: 8 }}>
            Current streak · {dash.stats.currentWinningStreak}W
          </p>
        ) : null}
      </section>

      <section className={`${pp.section} ${pp.sectionSeason}`} aria-labelledby="ppe-season">
        <h2 id="ppe-season" className={pp.kicker}>
          This season
        </h2>
        <div className={pp.seasonGrid}>
          <div className={pp.seasonCol}>
            <div className={pp.seasonValue}>{String(season.matches ?? 0)}</div>
            <div className={pp.seasonLabel}>Matches</div>
          </div>
          <div className={`${pp.seasonCol} ${pp.seasonColDivider}`}>
            <div className={pp.seasonValue}>{String(season.wins ?? 0)}</div>
            <div className={pp.seasonLabel}>Wins</div>
          </div>
          <div className={`${pp.seasonCol} ${pp.seasonColDivider}`}>
            <div className={pp.seasonValue}>{String(season.losses ?? 0)}</div>
            <div className={pp.seasonLabel}>Losses</div>
          </div>
          <div className={`${pp.seasonCol} ${pp.seasonColDivider}`}>
            <div
              className={`${pp.seasonValue}${
                season.winRate != null ? ` ${pp.seasonValueAccent}` : ""
              }`}
            >
              {season.winRate == null ? "—" : `${season.winRate}%`}
            </div>
            <div className={pp.seasonLabel}>Win rate</div>
          </div>
        </div>
      </section>

          {showCrossSport ? (
            <section className={styles.card}>
              <h2 className={styles.sectionTitle}>Sports</h2>
              <CrossSportSummary summary={dash.crossSport} />
            </section>
          ) : null}

          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>How you&apos;re playing</h2>
            <TRARecentFormBars
              form={ranking?.profileStats?.recentForm}
              letters={form}
              limit={5}
              padEmpty={false}
              empty={
                <TRASignatureEmptyState
                  icon="activity"
                  title={emptyCopy?.title || "No matches yet"}
                  description={emptyCopy?.body}
                />
              }
            />
          </section>

          {highlight ? (
            <article
              className={`${styles.card} ${styles.cardDark}`}
              data-share="achievement-highlight"
            >
              <span className={styles.badge}>Highlight</span>
              <h3 className={styles.nextTitle} style={{ color: "inherit" }}>
                {highlight.title}
              </h3>
              <p className={styles.nextMeta} style={{ color: "rgba(255,255,255,0.75)" }}>
                {highlight.detail}
              </p>
            </article>
          ) : null}

          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>Activity</h2>
            <ActivityCalendar calendar={dash.activityCalendar} />
          </section>

          <section>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Your story</h2>
            </div>
            <ActivityTimelineFeed
              events={(dash.activityTimeline || []).slice(0, 10)}
              emptyCopy={emptyCopy}
            />
          </section>

          <section>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Milestones</h2>
            </div>
            <MilestonesTimeline milestones={(dash.milestones || []).slice(0, 8)} />
          </section>
        </>
      ) : null}

      {tab === "history" ? (
        <section>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Recent matches</h2>
            <Link className={styles.linkAction} to="/profile/matches">
              Full history
            </Link>
          </div>
          {!matches.length ? (
            <TRASignatureEmptyState
              icon="trophy"
              title={emptyCopy?.title || "No matches yet"}
              description={emptyCopy?.body}
              action={
                emptyCopy?.ctaTo ? (
                  <Link className={styles.linkAction} to={emptyCopy.ctaTo}>
                    {emptyCopy.ctaLabel}
                  </Link>
                ) : null
              }
            />
          ) : (
            <ul className={styles.listStack}>
              {matches.map((m) => (
                <li key={m.matchId}>
                  <TRAMatchCard card={m} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ) : null}

      {tab === "stats" ? (
        <section aria-label="Career statistics">
          <h2 className={styles.sectionTitle}>
            Career{activeSportLabel ? ` · ${activeSportLabel}` : ""}
          </h2>
          <CareerStatsCards stats={dash.stats} tiles={dash.statTiles} />
          <div className={styles.sectionHead} style={{ marginTop: 20 }}>
            <h2 className={styles.sectionTitle}>Personal bests</h2>
          </div>
          <PersonalBestsGrid bests={dash.personalBests} />
          {ranking ? <RankingProfileBlock ranking={ranking} showAchievements={false} /> : null}
        </section>
      ) : null}

      {tab === "achievements" ? (
        <section>
          <AchievementGallery
            framework={dash.achievementFramework}
            emptyCopy={emptyCopy}
          />
          <div className={styles.sectionHead} style={{ marginTop: 24 }}>
            <h2 className={styles.sectionTitle}>Personal records</h2>
          </div>
          <PersonalRecordsGrid records={dash.personalRecords} />
        </section>
      ) : null}

      {error ? <p className={styles.error}>{error}</p> : null}
    </div>
  );
}
